import { core_v1_Rule } from '@antimatterhq/asl-api-ts-v1';
import { WritableDraft, produce } from 'immer';
import { formatDurationString } from '@antimatterhq/ui/utils/format';
import {
  getStreamTablesValues,
  setStreamTablesValues,
} from '../streamTable/streamTableHelper';
import { AddDetectionRuleSchemaValues } from '@/zod/schemas/detectionRules/AddDetectionRule';
import { keyValueArrayToObject } from '@/utils/util';
import { DurationUnit } from '@/components/radio/ProcessingSchedule/ProcessingScheduleRadioGroup';

export const emptyRuleData: core_v1_Rule = {
  spec: {
    schedule: {
      atLeastEvery: '10m',
      exactly: undefined,
      continuous: false,
      computeGroup: undefined,
      enabled: true,
    },
    input: {},
    output: {
      summary: '',
      context: {},
    },
    observables: [],
    collate: {
      collateOn: [],
      within: '',
      action: 'append',
    },
  },
};

export const getAddDetectionRuleFormInitialValues = (
  rule: core_v1_Rule,
): AddDetectionRuleSchemaValues => {
  // TODO: this isn't correct. Not sure why mitre changed to collection but we need to fix
  const {
    taxonomy = '',
    tactic = '',
    technique = '',
    subTechnique = '',
    techniqueId = '',
    subTechniqueId = '',
  } = rule.spec.metadata?.mitre?.[0] || {
    taxonomy: '',
    tactic: '',
    technique: '',
    subTechnique: '',
  };

  const schedule = rule?.spec.schedule?.continuous
    ? 'continuous'
    : rule.spec.schedule?.exactly
      ? 'cron'
      : 'duration';

  const durationObject = formatDurationString(
    rule.spec.schedule?.atLeastEvery ?? '',
  );

  const ruleInput =
    rule.spec.input?.batch?.sql || rule.spec.input?.batch?.custom
      ? 'batch'
      : 'streaming';

  const customNotebookPath =
    (ruleInput === 'streaming'
      ? rule.spec.input.stream?.custom?.notebook
      : rule.spec.input.batch?.custom?.notebook) || '';
  const customNotebookParameters =
    (ruleInput === 'streaming'
      ? rule.spec.input.stream?.custom?.options
      : rule.spec.input.batch?.custom?.options) || {};

  return {
    name: rule.metadata?.name ?? '',
    comment: rule.metadata?.comment ?? '',
    severity: rule.spec.metadata?.severity ?? '',
    category: rule.spec.metadata?.category ?? '',
    fidelity: rule.spec.metadata?.fidelity ?? '',
    objective: rule.spec.metadata?.objective || '',
    annotations: Object.entries(rule.metadata?.annotations ?? {}).map(
      ([key, value]) => ({
        key,
        value: String(value),
      }),
    ),
    taxonomy: taxonomy || '',
    tactic: tactic || '',
    technique: technique || '',
    techniqueId: techniqueId || '',
    subTechnique: subTechnique || '',
    subTechniqueId: subTechniqueId || '',
    schedule,
    duration: Number(durationObject?.value ?? '10'),
    durationUnit:
      schedule === 'duration' ? (durationObject?.unit as DurationUnit) : 'm',
    cron: rule.spec.schedule?.exactly ?? '',
    computeResource:
      rule.spec.schedule?.computeGroup === 'dedicated'
        ? 'dedicated'
        : rule.spec.schedule?.computeGroup === 'automatic' ||
            !rule.spec.schedule?.computeGroup
          ? 'automatic'
          : 'group',
    computeResourceGroup: rule.spec.schedule?.computeGroup ?? '',
    enabled: rule.spec.schedule?.enabled ?? true,
    ruleInput,
    notebookType: customNotebookPath ? 'custom' : 'inline',
    customNotebookPath,
    customNotebookParameters: Object.entries(customNotebookParameters).map(
      ([key, value]) => ({
        key: String(key),
        value: String(value),
      }),
    ),
    streamingTables: getStreamTablesValues(
      rule.spec.input?.stream?.tables ?? [],
      { streaming: true },
    ),
    streamingFilter: rule.spec.input?.stream?.filter ?? '',
    streamingSql: rule.spec.input?.stream?.sql ?? '',
    batchSql: rule.spec.input?.batch?.sql ?? '',
    observables: (rule.spec.observables || []).map((obs) => ({
      value: obs.value ?? '',
      kind: obs.kind ?? '',
      relationship: obs.relationship ?? '',
      risk: {
        impact: obs.risk?.impact ?? '',
        confidence: obs.risk?.confidence ?? '',
      },
    })),
    output: {
      summary: rule.spec.output?.summary ?? '',
      context: Object.entries(rule.spec.output?.context ?? {}).map(
        ([key, value]) => ({
          key: String(key),
          value: String(value),
        }),
      ),
    },
    collate: {
      on: rule.spec.collate?.collateOn ?? [],
      action: rule.spec.collate?.action ?? 'append',
      within: rule.spec.collate?.within || undefined,
    },
  };
};

export const convertAddDetectionRuleValuesToRule = (
  values: AddDetectionRuleSchemaValues,
): core_v1_Rule => {
  const {
    severity,
    category,
    fidelity,
    objective,
    taxonomy,
    tactic,
    technique,
    techniqueId,
    subTechnique,
    subTechniqueId,
    name,
    comment,
    schedule,
    computeResourceGroup,
    enabled,
    streamingTables,
    streamingFilter,
    streamingSql,
    batchSql,
    observables,
    output,
    collate,
    duration,
    durationUnit,
    cron,
    ruleInput,
    customNotebookPath,
    notebookType,
  } = values;

  return produce(emptyRuleData, (draft: WritableDraft<core_v1_Rule>) => {
    const isStreaming = ruleInput === 'streaming';
    const isNotebook = notebookType === 'custom';

    // draft.metadata
    const annotations = keyValueArrayToObject(values.annotations);
    draft.metadata = {
      name,
      comment,
      annotations,
    };

    // draft.spec.input
    const customNotebookParameters = keyValueArrayToObject(
      values.customNotebookParameters,
    );

    // reset these values first
    draft.spec.input.stream = undefined;
    draft.spec.input.batch = undefined;

    // set the input values
    if (isStreaming) {
      draft.spec.input.stream = {};

      if (isNotebook) {
        draft.spec.input.stream.custom = {
          notebook: customNotebookPath,
          options: customNotebookParameters,
        };
      } else {
        draft.spec.input.stream.filter = streamingFilter || undefined;
        draft.spec.input.stream.sql = streamingSql || undefined;
      }

      draft.spec.input.stream.tables = streamingTables.map((table) =>
        setStreamTablesValues(table),
      );
    } else {
      draft.spec.input.batch = {};
      if (isNotebook) {
        draft.spec.input.batch.custom = {
          notebook: customNotebookPath,
          options: customNotebookParameters,
        };
      } else {
        draft.spec.input.batch.sql = batchSql;
      }
    }

    // draft.spec.metadata
    draft.spec.metadata = {
      objective: objective || undefined,
      severity: severity || undefined,
      fidelity: fidelity || undefined,
      category: category || undefined,
      mitre: [
        {
          taxonomy: taxonomy ?? '',
          tactic: tactic ?? '',
          technique: technique ?? '',
          subTechnique: subTechnique ?? '',
          techniqueId: techniqueId ?? '',
          subTechniqueId: subTechniqueId ?? '',
        },
      ],
    };

    // draft.spec.schedule
    draft.spec.schedule = {
      continuous: schedule === 'continuous',
      atLeastEvery:
        schedule === 'duration'
          ? `${String(duration ?? 10)}${durationUnit ?? 'm'}`
          : undefined,
      exactly: schedule === 'cron' ? (cron ?? '') : undefined,
      computeGroup: computeResourceGroup ?? '',
      enabled,
    };

    // draft.spec.observables
    draft.spec.observables = observables.map((obs) => ({
      value: obs.value ?? '',
      kind: obs.kind ?? '',
      relationship: obs.relationship ?? '',
      risk: {
        impact: obs.risk?.impact ?? '',
        confidence: obs.risk?.confidence ?? '',
      },
    }));

    // draft.spec.output
    draft.spec.output = {
      summary: output.summary,
      context: keyValueArrayToObject(output.context),
    };

    // draft.spec.collate
    const filterCollate = collate.on.filter((q) => !!q) as string[];
    draft.spec.collate = filterCollate.length
      ? {
          collateOn: filterCollate,
          action: collate.action || undefined,
          within: collate.within || undefined,
        }
      : undefined;
  });
};
